import { Card, Flex, Form, Typography } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import React, { Suspense, useMemo } from "react"

import { TemplateDocumentFormLabel } from "../../DocumentPages/components/TemplateDocumentForm/TemplateDocumentFormLabel/TemplateDocumentFormLabel.tsx"
import { buildBlockVariants } from "../../DocumentPages/components/build-block-variants.tsx"
import { TEMPLATE_BLOCK_TYPE } from "../../constants/template-block-type.tsx"
import type { TemplateDocumentFormData } from "../../types/template-document-form-data.tsx"
import type {
  DocumentTemplateBlock,
  DocumentTemplateMutateData
} from "../../types/types.ts"

interface TemplateFormSecondStepProps {
  form: FormInstance<DocumentTemplateMutateData>
  disabled?: boolean
  // blocksLookup: Map<string, DocumentAvailableBlock>
}

export function TemplateFormSecondStep({
  form,
  disabled
  // blocksLookup
}: TemplateFormSecondStepProps) {
  const formType = form as unknown as FormInstance<TemplateDocumentFormData>
  const blocks = Form.useWatch("blocks", form) ?? form.getFieldValue("blocks")

  const variants = useMemo(
    () =>
      buildBlockVariants({
        form: formType,
        isTemplate: true,
        initialValues: {
          [TEMPLATE_BLOCK_TYPE.INITIAL_SIGNATURE]: "Initial",
          [TEMPLATE_BLOCK_TYPE.SIGNATURE_DATE]: "Authorized Signature"
        }
      }),
    [formType]
  )

  const removeBlock = (id: string) => {
    const blocks = form.getFieldValue("blocks") as DocumentTemplateBlock[]

    form.setFields([
      {
        name: "blocks",
        touched: true,
        value: blocks.filter((b) => b.id !== id)
      }
    ])
  }

  return (
    <Flex
      gap={"middle"}
      className={"overflow-auto tailwind-scroll"}
      flex={1}
      vertical
    >
      <Typography.Text type="secondary">
        {blocks?.length || 0} {blocks?.length === 1 ? "block" : "blocks"}
      </Typography.Text>
      {blocks?.map((block, i) => {
        const data = variants?.[block?.block_type]
        if (!data) return null
        const component = data?.component

        return (
          <Card
            key={block.id}
            size={"small"}
            classNames={{ header: "py-3 bg-gray-100 border-b" }}
            title={
              <TemplateDocumentFormLabel
                id={block?.id}
                index={i}
                form={formType}
                label={data?.label}
                alignment={data?.alignment}
                required={data?.required}
                titleReadonly={data?.titleReadonly}
                columns={data?.columns}
                onRemove={removeBlock}
              />
            }
          >
            <Suspense fallback={"Loading"}>
              {typeof component === "function"
                ? component({ block, disabled }, i)
                : component}
            </Suspense>
          </Card>
        )
      })}
    </Flex>
  )
}
